import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { embeddedTranslations } from '@/lib/translations';

type Lang = 'ar' | 'en';

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string, fallback?: string) => string;
  isLoading: boolean;
  dir: 'rtl' | 'ltr';
  dashboardMultiLangEnabled: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// ─── Module-level caches to prevent duplicate fetches ───
let _featureFlagCache: { enabled: boolean; ts: number } | null = null;
let _translationsCache: { lang: Lang; data: Record<string, string>; ts: number } | null = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    const saved = localStorage.getItem('app_lang');
    return (saved === 'en' ? 'en' : 'ar') as Lang;
  });
  const [translations, setTranslations] = useState<Record<string, string>>(() => {
    // Use cache if available and same lang
    if (_translationsCache && _translationsCache.lang === (localStorage.getItem('app_lang') || 'ar') && Date.now() - _translationsCache.ts < CACHE_TTL) {
      return _translationsCache.data;
    }
    return {};
  });
  const [isLoading, setIsLoading] = useState(!_translationsCache || _translationsCache.lang !== (localStorage.getItem('app_lang') || 'ar'));
  const [dashboardMultiLangEnabled, setDashboardMultiLangEnabled] = useState(() => {
    if (_featureFlagCache && Date.now() - _featureFlagCache.ts < CACHE_TTL) return _featureFlagCache.enabled;
    return true;
  });
  const flagFetched = useRef(false);

  const setLang = useCallback((newLang: Lang) => {
    setLangState(newLang);
    localStorage.setItem('app_lang', newLang);
    document.documentElement.lang = newLang;
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  }, []);

  // Fetch multi_language feature flag - once per session
  useEffect(() => {
    if (flagFetched.current) return;
    if (_featureFlagCache && Date.now() - _featureFlagCache.ts < CACHE_TTL) {
      setDashboardMultiLangEnabled(_featureFlagCache.enabled);
      if (!_featureFlagCache.enabled && lang !== 'ar') setLang('ar');
      flagFetched.current = true;
      return;
    }
    flagFetched.current = true;
    const fetchFlag = async () => {
      const { data } = await supabase
        .from('feature_flags')
        .select('enabled')
        .eq('key', 'multi_language')
        .maybeSingle();
      const enabled = data?.enabled ?? true;
      _featureFlagCache = { enabled, ts: Date.now() };
      setDashboardMultiLangEnabled(enabled);
      if (!enabled && lang !== 'ar') {
        setLang('ar');
      }
    };
    fetchFlag();
  }, []);

  // Force Arabic when feature is disabled (only for dashboard, not public menu)
  useEffect(() => {
    const isPublicMenu = window.location.pathname.startsWith('/menu/');
    if (!dashboardMultiLangEnabled && lang !== 'ar' && !isPublicMenu) {
      setLang('ar');
    }
  }, [dashboardMultiLangEnabled, lang, setLang]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  useEffect(() => {
    // Use cache if valid
    if (_translationsCache && _translationsCache.lang === lang && Date.now() - _translationsCache.ts < CACHE_TTL) {
      setTranslations(_translationsCache.data);
      setIsLoading(false);
      return;
    }

    const loadTranslations = async () => {
      setIsLoading(true);
      const { data } = await supabase
        .from('translations')
        .select('key, value')
        .eq('lang', lang);

      if (data) {
        const map: Record<string, string> = {};
        data.forEach((row) => {
          map[row.key] = row.value;
        });
        _translationsCache = { lang, data: map, ts: Date.now() };
        setTranslations(map);
      }
      setIsLoading(false);
    };
    loadTranslations();
  }, [lang]);

  const t = useCallback(
    (key: string, fallback?: string) => {
      if (translations[key]) return translations[key];
      if (embeddedTranslations[lang]?.[key]) return embeddedTranslations[lang][key];
      return fallback || key;
    },
    [translations, lang]
  );

  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isLoading, dir, dashboardMultiLangEnabled }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Fallback for HMR or edge cases where provider isn't ready
    return {
      lang: 'ar',
      setLang: () => {},
      t: (key: string, fallback?: string) => fallback || key,
      isLoading: false,
      dir: 'rtl',
      dashboardMultiLangEnabled: true,
    };
  }
  return context;
};
